import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { WineImage } from './types';

export function useUploadWineImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ wineId, file, kind }: { wineId: number; file: File; kind?: string }) => {
      const form = new FormData();
      form.append('file', file);
      if (kind) form.append('kind', kind);
      const { data } = await api.post<{ data: WineImage }>(
        `/wines/${wineId}/images`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['wine', vars.wineId] });
    },
  });
}

export function useUpdateWineImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      wineId,
      imageId,
      patch,
    }: {
      wineId: number;
      imageId: number;
      patch: { kind?: string; position?: number };
    }) => {
      const { data } = await api.patch<{ data: WineImage }>(
        `/wines/${wineId}/images/${imageId}`,
        patch,
      );
      return data.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['wine', vars.wineId] });
    },
  });
}

export function useDeleteWineImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ wineId, imageId }: { wineId: number; imageId: number }) => {
      await api.delete(`/wines/${wineId}/images/${imageId}`);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['wine', vars.wineId] });
    },
  });
}

export function useImportWineImages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ zip, mapping }: { zip: File; mapping: File }) => {
      const form = new FormData();
      form.append('zip', zip);
      form.append('mapping', mapping);
      const { data } = await api.post<{
        status: string;
        images_created?: number;
        rows_failed?: number;
        errors?: { row: number; reason: string }[];
        message?: string;
      }>('/wine-images/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wine'] });
      qc.invalidateQueries({ queryKey: ['wines'] });
    },
  });
}
